import { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons/faCircleCheck';
import { faCircleXmark } from '@fortawesome/pro-solid-svg-icons/faCircleXmark';
import { faGripVertical } from '@fortawesome/pro-solid-svg-icons/faGripVertical';
import { useColors } from '@osmosis/react-ui/hooks/useColors';
import { Icon } from '@osmosis/react-ui/components/Icon';
import { Flex } from '@osmosis/react-ui/components/Box';
import { useSchemeContext } from '@osmosis/react-ui/context/SchemeContext';
import { SxStyleProp } from 'rebass';
import { useQuizContext } from '../../../../context/QuizContext';
import { Item } from '../../../dnd/Item';
import { P } from '@osmosis/react-ui/components/Text';
import { ColorProps } from '@osmosis/shared/styles';

interface CustomRenderComponentProps {
  isReady: boolean;
  isCorrect: boolean | undefined;
  value: string;
  scheme: string;
  colors: ColorProps;
}

const CustomRenderComponent: React.FC<CustomRenderComponentProps> = ({
  isReady,
  isCorrect,
  value,
  scheme,
  colors,
}) => (
  <Flex
    alignItems="center"
    sx={{
      position: 'relative',
      justifyContent: 'space-between',
      width: '100%',
    }}
  >
    {isReady && (
      <Flex
        sx={{
          justifyContent: 'center',
          alignItems: 'center',
          position: 'absolute',
          top: -20,
          left: -25,
          color: isCorrect ? colors.positive : colors.negative,
          bg: 'bg-0',
          borderRadius: '50%',
          height: 28,
          width: 28,
        }}
        data-testid="answered-icon-container"
      >
        <Icon size="xl" icon={isCorrect ? faCircleCheck : faCircleXmark} />
      </Flex>
    )}

    {!isReady && <Icon color={colors.gray60} icon={faGripVertical} />}
    <P
      sx={{
        color: scheme === 'dark' ? colors.white : 'initial',
      }}
      ml={isReady ? 0 : 2}
      dangerouslySetInnerHTML={{ __html: `${value}` }}
    />
  </Flex>
);

/**
 * SentenceDnDItem.
 */
export const SentenceDnDItem = ({
  value,
  isDragging,
  setNodeRef,
  listeners,
  sx,
  dragOverlay,
  isCorrect,
}: {
  value: string;
  isDragging?: boolean;
  setNodeRef?: (element: HTMLElement) => void;
  listeners?: SyntheticListenerMap;
  sx?: SxStyleProp;
  dragOverlay?: boolean;
  isCorrect?: boolean;
}) => {
  const { isAnswered } = useQuizContext();

  const colors = useColors();

  const { scheme } = useSchemeContext();

  const isReady = isAnswered && isCorrect !== undefined;

  const bg = isCorrect ? colors.positive : colors.negative;

  return (
    <Item
      customRender={() => (
        <CustomRenderComponent
          isReady={isReady}
          isCorrect={isCorrect}
          value={value}
          scheme={scheme}
          colors={colors}
        />
      )}
      sx={{
        border: isReady ? 'none' : `1px solid ${colors.gray20}`,
        borderRadius: '5px',
        padding: '5px 10px',
        display: 'flex',
        background: isReady ? bg : colors['bg-0'],
        ...(isAnswered && {
          ':hover': {
            cursor: 'default',
          },
        }),
        ...sx,
      }}
      dragOverlay={dragOverlay}
      dragging={isDragging}
      // FIXME: any
      ref={setNodeRef as any}
      listeners={listeners}
    />
  );
};
